import {BrowserRouter, Routes, Route, Navigate, useNavigate} from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { rootStore } from './stores/RootStore';
import Login from './pages/login/Login';
import SignUp from './components/SignUp';
import { CircularProgress, Box, Backdrop } from '@mui/material';
import i18next from 'i18next';
import StoreMain from './pages/store/StoreMain';
import ProductForm from './pages/admin/Product/ProductForm';
import ProductSetManagement from './pages/admin/ProductSetManagement';
import ProductSetForm from './pages/admin/ProductSetForm';
import Layout from './components/Layout';
import ProductCategoryManagement from './pages/admin/ProductCategory/ProductCategoryManagement';
import ProductCategoryForm from './pages/admin/ProductCategoryForm';
import ProductManagement from './pages/admin/Product/ProductManagement';
import ProgressDialog from './components/ProgressDialog/ProgressDialog';
import { useStores } from './stores/useStores';
import UserManagement from "./pages/admin/User/UserManagement";
import UserDetail from "./pages/admin/User/UserDetail";
import DeliveryAddressList from './pages/purchase/DeliveryAddressList';
import DeliveryDateTime from './pages/purchase/DeliveryDateTime';
import OrderInput from './pages/purchase/OrderInput';
import OrderConfirm from './pages/purchase/OrderConfirm';
import OrderFinalConfirm from './pages/purchase/OrderFinalConfirm';
import OrderManagement from "./pages/order/OrderManagement";
import OrderDetail from "./pages/order/OrderDetail";
import PasswordReset from './pages/passwordreset/PasswordReset';
import InvoiceManagement from "./pages/invoice/InvoiceManagement";
import AnnouncementAddForm from './pages/admin/Announcement/AnnouncementAddForm';
import AnnouncementList from './pages/admin/Announcement/AnnouncementList';
import AnnouncementDetail from './pages/admin/Announcement/AnnouncementDetail';
import {useEffect} from "react";
import {useTranslation} from "react-i18next";
import axios from "axios";
import AnnouncementEditForm from './pages/admin/Announcement/AnnouncementEditForm';
import ProductMappingUserList from './pages/admin/ProductMapping/ProductMappingUserList';
import ProductMappingDetail from './pages/admin/ProductMapping/ProductMappingDetail';
import ServiceStatusPage from './pages/admin/ServiceStatus/ServiceStatusPage';

interface ProtectedRouteProps {
	children: React.ReactNode;
	allowPublic?: boolean;
	requiredRole?: string;
}

const ProtectedRoute = observer(({ children, allowPublic, requiredRole }: ProtectedRouteProps) => {
	const { authStore } = rootStore;
	const navigate = useNavigate();

	useEffect(() => {
		const axiosResponseInterceptor = (response) => {
			if (response.status === 401) {
				authStore.logout();
				navigate('/', { replace: true });
			}

			return response;
		};

		const axiosResponseErrorHandler = (error) => {
			const status = error.response?.status ?? error.status;
			if (status === 401) {
				const errorCode = error.response?.data?.errorCode;
				if (errorCode === 'USER_DISABLED') {
					alert(i18next.t('auth.account_disabled'));
				}
				authStore.logout();
				navigate('/', { replace: true });
				return new Promise(() => {});
			}
			if (status === 403) {
				navigate('/', { replace: true });
				return new Promise(() => {});
			}

			return Promise.reject(error);
		};

		const interceptorId = axios.interceptors.response.use(
			axiosResponseInterceptor,
			axiosResponseErrorHandler
		);

		return () => {
			axios.interceptors.response.eject(interceptorId);
		};
	}, []);

	if (allowPublic) {
		return <>{children}</>;
	}

	if (!authStore.isInitialized) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
				<CircularProgress />
			</Box>
		);
	}

	if (!authStore.isAuthenticated) {
		return <Navigate to="/login" replace />;
	}

	if (requiredRole && authStore.user?.level !== requiredRole) {
		return <Navigate to="/" replace />;
	}

	return <>{children}</>;
});

const App = observer(() => {
	const { viewStatusStore } = useStores();
	const { i18n } = useTranslation();

	useEffect(() => {
		document.title = i18n.language === 'ja' ? '玉井式教材 注文サイト' : 'Tamaishiki Mall';
	}, [i18n.language]);

	return (
		<>
			<BrowserRouter>
				<Routes>
					{/* Public routes */}
					<Route path="/" element={
						<Layout>
							<StoreMain />
						</Layout>
					} />
					<Route path="/login" element={
						<Layout>
							<Login />
						</Layout>
					} />
					<Route path="/signup" element={
						<Layout>
							<SignUp />
						</Layout>
					} />
					<Route path="/passwordreset" element={
						<Layout>
							<PasswordReset />
						</Layout>
					} />

					<Route path="/admin/announcements" element={
						<ProtectedRoute allowPublic>
							<Layout>
								<AnnouncementList />
							</Layout>
						</ProtectedRoute>
					} />
					<Route path="/admin/announcements/:id" element={
						<ProtectedRoute allowPublic>
							<Layout>
								<AnnouncementDetail />
							</Layout>
						</ProtectedRoute>
					} />

					{/* Protected routes */}
					<Route path="/dashboard" element={
						<ProtectedRoute>
							<Layout>
								<div>대시보드</div>
							</Layout>
						</ProtectedRoute>
					} />

					<Route path="/profile" element={
						<ProtectedRoute>
							<Layout>
								<UserDetail />
							</Layout>
						</ProtectedRoute>
					} />

					<Route path="/admin/users" element={
						<ProtectedRoute>
							<Layout>
								<UserManagement />
							</Layout>
						</ProtectedRoute>
					} />

					<Route path="/admin/users/:id" element={
						<ProtectedRoute>
							<Layout>
								<UserDetail />
							</Layout>
						</ProtectedRoute>
					} />

					<Route path="/admin/product-categories" element={
						<ProtectedRoute requiredRole="ADMIN">
							<Layout>
								<ProductCategoryManagement />
							</Layout>
						</ProtectedRoute>
					} />

					<Route path="/admin/product-categories/create" element={
						<ProtectedRoute requiredRole="ADMIN">
							<Layout>
								<ProductCategoryForm />
							</Layout>
						</ProtectedRoute>
					} />

					<Route path="/admin/product-categories/edit/:id" element={
						<ProtectedRoute requiredRole="ADMIN">
							<Layout>
								<ProductCategoryForm />
							</Layout>
						</ProtectedRoute>
					} />

					<Route path="/admin/products" element={
						<ProtectedRoute requiredRole="ADMIN">
							<Layout>
								<ProductManagement />
							</Layout>
						</ProtectedRoute>
					} />

					<Route path="/admin/products/create" element={
						<ProtectedRoute requiredRole="ADMIN">
							<Layout>
								<ProductForm />
							</Layout>
						</ProtectedRoute>
					} />
					<Route path="/admin/products/edit/:id" element={
						<ProtectedRoute requiredRole="ADMIN">
							<Layout>
								<ProductForm />
							</Layout>
						</ProtectedRoute>
					} />
					<Route path="/admin/product-sets" element={
						<ProtectedRoute requiredRole="ADMIN">
							<Layout>
								<ProductSetManagement />
							</Layout>
						</ProtectedRoute>
					} />
					<Route path="/admin/product-sets/create" element={
						<ProtectedRoute requiredRole="ADMIN">
							<Layout>
								<ProductSetForm />
							</Layout>
						</ProtectedRoute>
					} />
					<Route path="/admin/product-sets/edit/:id" element={
						<ProtectedRoute requiredRole="ADMIN">
							<Layout>
								<ProductSetForm />
							</Layout>
						</ProtectedRoute>
					} />
					<Route path="/purchase/delivery-address" element={
						<ProtectedRoute>
							<Layout>
								<DeliveryAddressList />
							</Layout>
						</ProtectedRoute>
					} />
					<Route path="/purchase/delivery-datetime" element={
						<ProtectedRoute>
							<Layout>
								<DeliveryDateTime />
							</Layout>
						</ProtectedRoute>
					} />
					<Route path="/purchase/order-input" element={
						<ProtectedRoute>
							<Layout>
								<OrderInput />
							</Layout>
						</ProtectedRoute>
					} />
					<Route path="/purchase/order-confirm" element={
						<ProtectedRoute>
							<Layout>
								<OrderConfirm />
							</Layout>
						</ProtectedRoute>
					} />
					<Route path="/purchase/order-final-confirm" element={
						<ProtectedRoute>
							<Layout>
								<OrderFinalConfirm />
							</Layout>
						</ProtectedRoute>
					} />
					<Route path="/orders" element={
						<ProtectedRoute>
							<Layout>
								<OrderManagement />
							</Layout>
						</ProtectedRoute>
					} />
					<Route path="/orders/:id" element={
						<ProtectedRoute>
							<Layout>
								<OrderDetail />
							</Layout>
						</ProtectedRoute>
					} />
					<Route path="/invoices" element={
						<ProtectedRoute>
							<Layout>
								<InvoiceManagement />
							</Layout>
						</ProtectedRoute>
					} />
					<Route path="/admin/announcements/add" element={
						<ProtectedRoute requiredRole="ADMIN">
							<Layout>
								<AnnouncementAddForm />
							</Layout>
						</ProtectedRoute>
					} />
					<Route path="/admin/announcements/edit/:id" element={
						<ProtectedRoute requiredRole="ADMIN">
							<Layout>
								<AnnouncementEditForm />
							</Layout>
						</ProtectedRoute>
					} />
					<Route path="/admin/product-mappings" element={
						<ProtectedRoute requiredRole="ADMIN">
							<Layout>
								<ProductMappingUserList />
							</Layout>
						</ProtectedRoute>
					} />
					<Route path="/admin/product-mappings/:id" element={
						<ProtectedRoute requiredRole="ADMIN">
							<Layout>
								<ProductMappingDetail />
							</Layout>
						</ProtectedRoute>
					} />
					<Route path="/admin/service-status" element={
						<ProtectedRoute requiredRole="ADMIN">
							<Layout>
								<ServiceStatusPage />
							</Layout>
						</ProtectedRoute>
					} />

					{/* Add more protected routes here */}

					{/* 정의되지 않은 경로는 홈으로 리다이렉트 */}
					<Route path="*" element={<Navigate to="/" replace />} />
				</Routes>
			</BrowserRouter>
			<ProgressDialog
				open={viewStatusStore.progressStatus.open}
				title={viewStatusStore.progressStatus.title}
				progress={viewStatusStore.progressStatus.progress}
				total={viewStatusStore.progressStatus.total}
				messageFunc={viewStatusStore.progressStatus.messageFunc}
			/>
			<Backdrop
				sx={{
					color: '#fff',
					zIndex: (theme) => theme.zIndex.drawer + 1,
					display: 'flex',
					flexDirection: 'column',
					gap: 2,
					backgroundColor: 'rgba(0, 0, 0, 0.7)'
				}}
				open={viewStatusStore.loadingStatus.open}
			>
				<CircularProgress color="inherit" />
				{viewStatusStore.loadingStatus.message && (
					<Box sx={{ color: 'white', mt: 2 }}>
						{viewStatusStore.loadingStatus.message}
					</Box>
				)}
			</Backdrop>
		</>
	);
});

export default App;
